/* global Engage, PubSub */

/**
 * @description Initializes the core messaging library for the Engage Locator.
 */
void (function initializeLocatorMessaging() {
	'use strict';

	if (window.PubSub === undefined) {
		const retryDelay = 20;
		setTimeout(initializeLocatorMessaging, retryDelay);
		return;
	}

	window.Engage = window.Engage || {};

	/**
	 * @namespace Window.Engage.Locator
	 */
	Engage.Locator = Engage.Locator || {};

	/**
	 * @namespace Window.Engage.Locator.Messaging
	 */
	Engage.Locator.Messaging = Engage.Locator.Messaging || {};

	/**
	 * @private
	 * @constant {string} locatorMessagePrefix - Defines the root prefix for all messages inside the Engage Locator.
	 * @memberof Window.Engage.Locator.Messaging
	 */
	const locatorMessagePrefix = 'Engage.Locator';

	/**
	 * @private
	 * @function startsWithMessagePrefix
	 * @description Searched the provided string to see if it begins with the Engage Locator messaging prefix.
	 * @memberof Window.Engage.Locator.Messaging
	 * @example
	 * // Returns True
	 * startsWithMessagePrefix('Engage.Locator.ValidateZipCode');
	 * @example
	 * // Returns False
	 * startsWithMessagePrefix('Publish.Invalid.Events');
	 * @param  {string} searchStr - The string to be searched.
	 * @returns {bool} True if the provided string is prefixed with the Engage Locator messaging prefix; otherwise, False.
	 */
	function startsWithMessagePrefix(searchStr) {
		return (
			searchStr.substr(0, locatorMessagePrefix.length) ===
			locatorMessagePrefix
		);
	}

	/**
	 * @name locatorMessagePrefix
	 * @description Defines the root prefix for all messages inside the Engage Locator.
	 * @memberof Window.Engage.Locator.Messaging
	 */
	Engage.Locator.Messaging.locatorMessagePrefix = locatorMessagePrefix;

	/**
	 * @name messageTypes
	 * @memberof Window.Engage.Locator.Messaging
	 * @description Defines the names of the messages being sent throughout the Engage Locator.
	 * @property {string} validateZipCode - Requests Geocoding of address for validation.
	 * @property {string} zipCodeValidationResult - Response to {validateZipCode} geocode request.
	 * @property {string} locationUpdate - Inform all components of a new location for a radius search.
	 * @property {string} resetSearchFrom - Inform all components the search filters have been reset.
	 * @property {string} newQueryFilters - Inform all components of new search query filters.
	 * @property {string} boundingBoxUpdate - Inform all components the map was moved and provide the bound box.
	 * @property {string} newLocations - Inform all components of a new list of locations.
	 * @property {string} showLoading - Inform all components that they should be displaying a loading indicator.
	 * @property {string} advancedSearch - Inform all components that a new advanced search is being started.
	 * @property {string} trackEvent - Inform all components that an event should be tracked.
	 */
	Engage.Locator.Messaging.messageTypes = {
		validateZipCode: `${locatorMessagePrefix}.ValidateZipCode`,
		zipCodeValidationResult: `${locatorMessagePrefix}.ZipCodeValidationResult`,
		locationUpdate: `${locatorMessagePrefix}.LocationUpdate`,
		resetSearchFrom: `${locatorMessagePrefix}.ResetSearchForm`,
		newQueryFilters: `${locatorMessagePrefix}.NewQueryFilters`,
		boundingBoxUpdate: `${locatorMessagePrefix}.BoundingBoxUpdate`,
		newLocations: `${locatorMessagePrefix}.NewLocations`,
		showLoading: `${locatorMessagePrefix}.ShowLoading`,
		advancedSearch: `${locatorMessagePrefix}.AdvancedSearch`,
		trackEvent: `${locatorMessagePrefix}.TrackEvent`,
		resetCustomFilters: `${locatorMessagePrefix}.ResetCustomFilters`,
		refreshLocation: `${locatorMessagePrefix}.RefreshLocation`,
	};

	/**
	 * @function publish
	 * @memberof Window.Engage.Locator.Messaging
	 * @description Publishs the message only if the message type is an Engage Locator message.
	 * @example
	 * const messaging = window.Engage.Locator.Messaging;
	 * messaging.publish(messaging.messageTypes.validateZipCode, { address: 'St. Louis, MO 63131', });
	 * @param  {string} messageType - The type of message requesting publish.
	 * @param  {Object} payload - The object payload for the message requesting publish.
	 */
	Engage.Locator.Messaging.publish = (messageType, payload) => {
		if (!startsWithMessagePrefix(messageType)) {
			return;
		}

		PubSub.publish(messageType, payload);
	};

	/**
	 * @global
	 * @description Defines the structure of the callback for all Engage Locator messaging subscriptions.
	 * @callback subscriptionCallback
	 * @param {string} messageType - The type of message requesting publish.
	 * @param {Object} payload - The object payload for the message requesting publish.
	 */

	/**
	 * @function subscribe
	 * @memberof Window.Engage.Locator.Messaging
	 * @description Subscribes to all messages of type {messageType} if the message type is an Engage Locator message.
	 * @example
	 * const messaging = window.Engage.Locator.Messaging;
	 * messaging.subscribe(messaging.messageTypes.validateZipCode, (messageType, { address, }) => {
	 *      if (messageType !== messaging.messageTypes.validateZipCode || !address) {
	 *        return;
	 *      }
	 *
	 *      // Do what you need.
	 * });
	 * @param  {string} messageType - The type of message requesting subscriptions.
	 * @param  {subscriptionCallback} subscriber - The callback that handles the response to messages being published.
	 */
	Engage.Locator.Messaging.subscribe = (messageType, subscriber) => {
		if (!startsWithMessagePrefix(messageType)) {
			return;
		}

		PubSub.subscribe(messageType, subscriber);
	};

	Engage.Locator.Messaging.unsubscribe = PubSub.unsubscribe;
})();
